import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Button,
    Typography,
    FormHelperText
} from "@material-ui/core";

import License, { SpectralService } from "spectral-core/models/License";
import User from "spectral-core/models/User";
import SpectralModel from "spectral-core/models/SpectralModel";
import { LicenseForm } from "components/CardLicense/CardLicense";
import { useRootSelector } from "hooks/reduxHooks";
import { useUserLoggedIn } from "hooks/userHooks";
import { getModels, getUsersByCompany } from "spectral-core/api";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import intl from "react-intl-universal";

interface LicenseDialogProps {
    open: boolean;
    license: License;
    expiresAtDisabled: boolean;
    onClose: () => void;
    onSave: (licenseForm: LicenseForm) => void;
    onCancel: () => void;
};

const toLicenseForm = (license: License): LicenseForm => {
    return {
        name: license.name,
        description: license.description,
        price: license.price,
        expiresAt: license.expiresAt,
        service: license.service,
        users: license.users ? JSON.parse(license.users) : [],
        isDeviceRestrict: license.isDeviceRestrict,
        devices: license.devices ? JSON.parse(license.devices) : [],
        isModelRestrict: license.isModelRestrict,
        models: license.models ? JSON.parse(license.models) : [],
        computerMac: license.computerMac
    };
};

export default function LicenseDialog({
    open,
    license,
    expiresAtDisabled,
    onClose,
    onSave,
    onCancel,
}: LicenseDialogProps) {

    const [formData, setFormData] = useState<LicenseForm>(toLicenseForm(license));
    const [currentFormData, setCurrentFormData] = useState<LicenseForm>(formData);

    const [users, setUsers] = useState<User[]>([]);
    const [models, setModels] = useState<SpectralModel[]>([]);

    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const accessToken = useRootSelector(
        (state) => state.user.currentToken?.accessToken
    );

    const isLoggedIn = useUserLoggedIn();

    useEffect(() => {
        async function getUsersFromApi(){
            if (!isLoggedIn) return;

            if (users.length > 0) return;

            try {
                const usersFromApi = await getUsersByCompany(accessToken!);
                setUsers(usersFromApi);
            } catch (err) {
                console.log(err);
            }
        }
    
        async function getModelsFromApi() {
            if (!isLoggedIn) return;

            if (models.length > 0) return;

            try {
                const modelsFromApi = await getModels(accessToken!);
                setModels(modelsFromApi);
            } catch (err) {
                console.log(err);
            }
        }

        getUsersFromApi();
        getModelsFromApi();
    }, [accessToken, isLoggedIn]);

    const handleChange = (
        e : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> 
    ) => {
        const { name, value } = e.target;
        
        setFormData((prev) => ({
            ...prev,
            [name]: name === "price" ? Number(value) : value
        }));
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    const handleSelectChange = (
            e: React.ChangeEvent<{ value: unknown }>
        ) => {
            setFormData((prev) => ({
                ...prev,
                service: e.target.value as number,
            }));
    };
    
    const handleListToggle = (field: "users" | "models", value: string) => {
        setFormData((prev) => {
            const currentList = prev[field];
            const newList = currentList.includes(value)
                ? currentList.filter((x) => x !== value)
                : [...currentList, value];
            return { ...prev, [field]: newList };
        });
    };

    const validate = () => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.name || formData.name.length < 3){
            newErrors.name = intl.get("license.nameValidation")
        } 

        if (!formData.price || formData.price <= 0){
            newErrors.price = intl.get("license.priceValidation");
        }

        if (formData.users.length === 0) {
            newErrors.users = intl.get("license.usersValidation");
        }

        if (!formData.expiresAt) {
            newErrors.expiresAt = intl.get("license.expiresAtRequired");
        } else {
            const expiresDate = new Date(formData.expiresAt);
            const today = new Date();

            if (expiresDate <= today) {
                newErrors.expiresAt = intl.get("license.expiresAtValidation");
            }
        }

        if (formData.isDeviceRestrict && (!formData.devices || formData.devices.length === 0)) {
            newErrors.devices = intl.get("license.devicesValidation");
        }

        if (formData.isModelRestrict && formData.models.length === 0) {
            newErrors.models = intl.get("license.modelsValidation");
        }

        if (formData.service === SpectralService.VisionMachine) {
            if (!formData.computerMac) {
                newErrors.computerMac = intl.get("license.computerMacRequired");
            } else if (formData.computerMac.length !== 12) {
                newErrors.computerMac = intl.get("license.computerMacValidation");
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const saveButtonHandleClick = () => {
        if (!validate()) return;
        onSave(formData);
        setCurrentFormData(formData);
    }

    const cancelButtonHandleClick = () => {
        onCancel();
        setFormData(currentFormData);
        setErrors({});
    }

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="sm" fullWidth
        >
            <DialogTitle style={{ fontWeight: 600 }}>{intl.get("license.license")}</DialogTitle>
            <DialogContent dividers>
                <GridContainer spacing={2}>
                    {/*@ts-ignore*/}
                    <GridItem xs={12} style={{ marginBottom: 4 }}>
                        <TextField
                            label={intl.get("license.name")}
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            error={!!errors.name}
                            helperText={errors.name}
                            fullWidth
                        />
                    </GridItem>
                    {/*@ts-ignore*/}
                    <GridItem xs={12} style={{ marginBottom: 12 }}>
                        <TextField
                            label={intl.get("license.description")}
                            type="text"
                            multiline={true}
                            rows={2}
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            error={!!errors.description}
                            helperText={errors.description}
                            fullWidth
                        />
                    </GridItem>
                    {/*@ts-ignore*/}
                    <GridItem xs={6}>
                        <TextField
                            label={intl.get("license.price")}
                            type="number"
                            name="price"
                            value={formData.price}
                            onChange={handleChange}
                            error={!!errors.price}
                            helperText={errors.price}
                            fullWidth
                        />
                    </GridItem>
                    {/*@ts-ignore*/}
                    <GridItem xs={6}>
                        <TextField
                            label={intl.get("license.expiresAt")}
                            name="expiresAt"
                            type="date"
                            value={
                                formData.expiresAt 
                                ? new Date(formData.expiresAt).toISOString().split("T")[0] 
                                : ""}
                            onChange={handleChange}
                            disabled={expiresAtDisabled}
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.expiresAt}
                            helperText={errors.expiresAt}
                            fullWidth
                        />
                    </GridItem>
                    {/*@ts-ignore*/}
                    <GridItem xs={12}>
                        <FormControl fullWidth style={{ marginTop: 12 }}>
                            <InputLabel>{intl.get("license.service")}</InputLabel>
                            <Select value={formData.service} onChange={handleSelectChange}>
                                {Object.entries(SpectralService)
                                    .filter(([key]) => isNaN(Number(key)))
                                    .map(([key, value]) => (
                                        <MenuItem key={value} value={value}>
                                            {key}
                                        </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </GridItem>
                    {/*@ts-ignore*/}
                    <GridItem xs={12}>
                        <FormControl
                            component="fieldset"
                            error={!!errors.users}>
                            <Typography 
                                variant="subtitle2" 
                                style={{ marginTop: 12, marginBottom: 4 }}>
                                    {intl.get("license.users")}
                            </Typography>
                            <div style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #ccc', borderRadius: 4, padding: 4 }}>
                                <FormGroup>
                                    {users.map((u) => (
                                        <FormControlLabel
                                            key={u.email}
                                            control={
                                            <Checkbox
                                                checked={formData.users.includes(u.email)}
                                                onChange={() => handleListToggle("users", u.email)}
                                                color="primary"
                                        />
                                    }
                                    label={u.email}
                                    />
                                ))}
                                </FormGroup>
                            </div>
                            {errors.users && (
                                <FormHelperText>{errors.users}</FormHelperText>
                            )}
                        </FormControl>
                    </GridItem>

                    {/*@ts-ignore*/}
                    <GridItem xs={12}>
                        {formData.service !== 1 && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.isDeviceRestrict}
                                        name="isDeviceRestrict"
                                        onChange={handleCheckboxChange}
                                        color="primary"
                                    />
                                }
                                label={intl.get("license.deviceRestrict")}
                                style={{ marginTop: 12 }}
                            />

                        )}
                    </GridItem>
                    {/*@ts-ignore*/}
                    <GridItem xs={12}>
                        {formData.service !== 1 && (
                            <TextField
                                value={formData.devices}
                                name="devices"
                                label={intl.get("license.devicesSeparatedByCommas")}
                                onChange={handleChange}
                                disabled={!formData.isDeviceRestrict}
                                error={!!errors.devices}
                                helperText={errors.devices}
                                fullWidth
                            />
                        )}
                    </GridItem>
                    {/*@ts-ignore*/}
                    <GridItem xs={12}>
                        {formData.service !== 1 && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.isModelRestrict}
                                        name="isModelRestrict"
                                        onChange={handleCheckboxChange}
                                        color="primary"
                                    />
                                }
                                label={intl.get("license.modelRestrict")}
                                style={{ marginTop: 12 }}
                            />
                        )}
                    </GridItem>

                    {/*@ts-ignore*/}
                    <GridItem xs={12}>
                        {formData.service !== 1 && (
                            <FormControl
                                error={!!errors.models}>
                                <Typography
                                    variant="subtitle2"
                                    style={{ marginBottom: 4 }}
                                >
                                    {intl.get("license.models")}
                                </Typography>
                                <div 
                                    style={{ 
                                        maxHeight: 150, 
                                        overflowY: 'auto', 
                                        border: '1px solid #ccc', 
                                        borderRadius: 4, 
                                        padding: 4,  
                                    }}
                                >
                                    <FormGroup>
                                        {models.map((m) => (
                                            <FormControlLabel
                                                key={m.rowKey}
                                                control={
                                                <Checkbox
                                                    checked={formData.models.includes(m.rowKey)}
                                                    onChange={() => handleListToggle("models", m.rowKey)}
                                                    disabled={!formData.isModelRestrict}
                                                />
                                            }
                                            label={`${m.applicationName} - ${m.parameterName}`}
                                            />
                                        ))}
                                    </FormGroup>
                                </div>
                                {errors.models && (
                                    <FormHelperText>{errors.models}</FormHelperText>
                                )}
                            </FormControl>
                        )}
                    </GridItem>
                    {/*@ts-ignore*/}
                    <GridItem xs={12}>
                        {formData.service !== 0 && (
                            <TextField
                                label={intl.get("license.computerMac")}
                                name="computerMac"
                                value={formData.computerMac}
                                style={{ marginTop: 12 }}
                                onChange={handleChange}
                                error={!!errors.computerMac}
                                helperText={errors.computerMac}
                                fullWidth
                            />
                        )}
                    </GridItem>
                </GridContainer>
            
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setFormData(toLicenseForm(license))}
                    variant="outlined"
                    color="default">
                        {intl.get("license.undo")}
                    </Button>
                <div style={{flex: '1 0 0'}} />
                <Button 
                    onClick={cancelButtonHandleClick} 
                    variant="outlined" 
                    color="secondary">
                        {intl.get("license.cancel")}
                </Button>
                <Button 
                    onClick={saveButtonHandleClick} 
                    variant="contained" 
                    style={{ backgroundColor: "#00acc1", color: "#fff" }}>
                        {intl.get("license.save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
